import type { PropsWithChildren, ReactNode } from "react";
import { useReducer } from "react";

import type { ModalDialogProps } from "./ModalDialog";
import { ModalDialog } from "./ModalDialog";
import { ModalDialogContext } from "./ModalDialogContext";

const DEFAULT_MODAL_ID = "default";

type Action =
  | { type: "open"; id?: string; modalPropsOrContent: ModalDialogProps | ReactNode }
  | { type: "close"; id?: string; reason: "backdropClick" | "escapeKeyDown" | "requested" }
  | { type: "error"; id?: string; message: ReactNode; title?: ReactNode }
  | { type: "destroy"; id?: string }
  | { type: "setClosable"; id: string; closable: boolean };

type State = Record<string, ModalDialogProps & { closable?: boolean }>;

const reducer = (state: State, action: Action) => {
  const { id = DEFAULT_MODAL_ID } = action;

  switch (action.type) {
    case "open": {
      const { modalPropsOrContent } = action;

      const modalProps =
        !!modalPropsOrContent && typeof modalPropsOrContent === "object" && "children" in modalPropsOrContent
          ? (modalPropsOrContent as ModalDialogProps)
          : ({ children: modalPropsOrContent } as ModalDialogProps);

      return {
        ...state,
        [id]: { ...modalProps, open: true, closable: true },
      };
    }

    case "close": {
      if (!state[id]) return state;

      if (!state[id].closable) return state;

      const { reason } = action;

      state[id].onClose?.({}, reason === "requested" ? "backdropClick" : reason);

      return {
        ...state,
        [id]: { ...state[id], open: false },
      };
    }

    case "error": {
      const { message, title } = action;

      return {
        ...state,
        [id]: {
          ...state[id],
          error: {
            buttons: "showClose",
            open: true,
            header: title,
            children: message,
          },
        },
      };
    }

    case "destroy": {
      if (!state[id]) {
        console.warn(`Unable to destroy Modal Dialog with id: ${id}, does not exist.`);
      }

      const updated = { ...state };

      if (id !== DEFAULT_MODAL_ID) {
        // automatically destroy custom modals on close to cleanup any loose ends.
        delete updated[id];
      }

      return updated;
    }

    case "setClosable": {
      const { closable } = action;

      return {
        ...state,
        [id]: { ...state[id], closable },
      };
    }

    default: {
      return state;
    }
  }
};

export const ModalDialogContextProvider = ({ children }: PropsWithChildren) => {
  const [dialogs, dispatch] = useReducer(reducer, {}, () => ({ [DEFAULT_MODAL_ID]: { open: false, header: null } }));

  return (
    <ModalDialogContext.Provider
      value={{
        openModalDialog: (modalPropsOrContent, id) => dispatch({ type: "open", id, modalPropsOrContent }),
        closeModalDialog: (id) => dispatch({ type: "close", id, reason: "requested" }),
        openErrorDialog: (message, title) => dispatch({ type: "error", message, title }),
        setDialogClosable: (id, closable) => dispatch({ type: "setClosable", id, closable }),
      }}
    >
      {children}

      {Object.keys(dialogs).map((id) => {
        const { children: content, closable: _, ...modalProps } = dialogs[id];

        return (
          <ModalDialog
            key={id}
            {...modalProps}
            onClose={(_event, reason) => dispatch({ type: "close", id, reason })}
            onTransitionExited={() => {
              if (!modalProps.open && id !== DEFAULT_MODAL_ID) {
                dispatch({ type: "destroy", id });
              }
            }}
          >
            {content}
          </ModalDialog>
        );
      })}
    </ModalDialogContext.Provider>
  );
};
